import { useEffect, useState } from "react";
import { Button, Form, Input, message } from "antd";
import { useNavigate } from "react-router-dom";
import PageLayout from "@layouts/PageLayout";
import { checkoutOrder, confirmOrder, formatInr } from "@services/shop.service";
import { fetchCart } from "@store/cartSlice";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import "./shop.css";

function loadRazorpay() {
  return new Promise((resolve) => {
    if ((window as any).Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function CheckoutPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const cart = useAppSelector((state) => state.cart);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    dispatch(fetchCart());
  }, [dispatch]);

  const pay = async (values: any) => {
    setPaying(true);
    try {
      const ready = await loadRazorpay();
      if (!ready) {
        message.error("Could not load Razorpay checkout");
        return;
      }
      const session = await checkoutOrder(values);
      const key = session?.key || process.env.REACT_APP_RAZORPAY_KEY;
      const amount = session?.amount || Math.round(Number(cart.subtotal) * 100);
      if (!key || !amount) {
        message.error("Payment could not open");
        return;
      }
      const options: any = {
        key,
        amount,
        currency: session?.currency || "INR",
        name: "Bazaar",
        description: "Order payment",
        ...(session?.razorpay_order_id ? { order_id: session.razorpay_order_id } : {}),
        handler: async (response: any) => {
          await confirmOrder({
            order_id: session.order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
          });
          dispatch(fetchCart());
          message.success("Payment received");
          navigate("/orders");
        },
        prefill: {
          name: values.shipping_name,
          contact: values.phone,
        },
        theme: { color: "#111111" },
      };
      const checkout = new (window as any).Razorpay(options);
      checkout.on("payment.failed", () => message.error("Payment failed"));
      checkout.open();
    } catch {
      return;
    } finally {
      setPaying(false);
    }
  };

  return (
    <PageLayout>
      <div className="shop-page" style={{ maxWidth: 640 }}>
        <div className="shop-hero">
          <div>
            <h2>Checkout</h2>
            <p>{cart.itemCount ? `Pay ${formatInr(cart.subtotal)}` : "Your cart is empty."}</p>
          </div>
        </div>
        {!cart.itemCount ? (
          <div className="surface">
            <Button type="primary" onClick={() => navigate("/")}>Continue shopping</Button>
          </div>
        ) : (
        <Form className="surface" layout="vertical" onFinish={pay} requiredMark={false}>
          <Form.Item name="shipping_name" label="Full name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label="Address" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="city" label="City">
            <Input />
          </Form.Item>
          <Form.Item name="pincode" label="Pincode">
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={paying}>
            Pay {formatInr(cart.subtotal)}
          </Button>
        </Form>
        )}
      </div>
    </PageLayout>
  );
}

export default CheckoutPage;
